import React, { useMemo, useState } from "react";
import { Search, Car as CarIcon, Wrench, ChevronDown, ChevronRight, Plus, AlertTriangle, Package } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from "../../components/ui/label";

/**
 * CarPartsView — vizualizare dedicată pentru piese de schimb auto.
 * Grupează item-urile (item_type='piesa_auto') după linked_car_id.
 * Mașinile cu cele mai multe piese apar primele. Piesele nealocate
 * (fără linked_car_id) sunt grupate într-o secțiune separată „Universale / Nealocate".
 *
 * Props:
 *  - items: lista globală inventar (filtrăm intern doar piesa_auto)
 *  - cars: lista mașini active
 *  - onAddPart(initialCarId): callback ce deschide ItemDialog cu item_type='piesa_auto'
 *  - onEdit(item): editează un rând existent
 *  - onChanged(): refresh după acțiuni
 */
export default function CarPartsView({ items, cars = [], initialCarFilter, onAddPart, onEdit, onChanged }) {
  const [search, setSearch] = useState("");
  const [carFilter, setCarFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set()); // car_id sau "__none__"

  // Apply car filter passed from URL once on mount
  React.useEffect(() => {
    if (initialCarFilter) {
      setCarFilter(initialCarFilter);
      setExpanded(new Set([initialCarFilter]));
    }
  }, [initialCarFilter]);

  const parts = useMemo(() => items.filter((i) => (i.item_type || "") === "piesa_auto"), [items]);

  const carIndex = useMemo(() => {
    const m = new Map();
    (cars || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [cars]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return parts.filter((p) => {
      if (carFilter !== "all") {
        if (carFilter === "__none__") {
          if (p.linked_car_id) return false;
        } else if (p.linked_car_id !== carFilter) return false;
      }
      if (statusFilter !== "all") {
        const isLow = (Number(p.quantity) || 0) <= 1;
        if (statusFilter === "low" && !isLow) return false;
        if (statusFilter === "defect" && p.status !== "defect") return false;
        if (statusFilter === "ok" && (p.status === "defect" || isLow)) return false;
      }
      if (!q) return true;
      const car = carIndex.get(p.linked_car_id || "");
      const carBlob = car ? `${car.marca || ""} ${car.model || ""} ${car.numar_inmatriculare || ""}` : "";
      const blob = `${p.name || ""} ${p.serial || ""} ${p.supplier || ""} ${p.equipment_category || ""} ${carBlob}`.toLowerCase();
      return blob.includes(q);
    });
  }, [parts, search, carFilter, statusFilter, carIndex]);

  // Group by linked_car_id
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      const key = p.linked_car_id || "__none__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    // Sort items per group by name
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));
    }
    // Convert to array sorted by count DESC then by car label
    return Array.from(map.entries())
      .map(([carId, list]) => {
        const car = carIndex.get(carId);
        const label = car
          ? `${car.marca || ""} ${car.model || ""}`.trim() + (car.numar_inmatriculare ? ` (${car.numar_inmatriculare})` : "")
          : "Universale / Nealocate";
        return { carId, car, label: label || "—", items: list };
      })
      .sort((a, b) => {
        if (a.carId === "__none__") return 1;
        if (b.carId === "__none__") return -1;
        if (b.items.length !== a.items.length) return b.items.length - a.items.length;
        return (a.label || "").localeCompare(b.label || "", "ro");
      });
  }, [filtered, carIndex]);

  const kpi = useMemo(() => {
    const totalQty = parts.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
    const carsWithParts = new Set(parts.filter((p) => p.linked_car_id).map((p) => p.linked_car_id)).size;
    const orphan = parts.filter((p) => !p.linked_car_id).length;
    const low = parts.filter((p) => (Number(p.quantity) || 0) <= 1).length;
    const defect = parts.filter((p) => p.status === "defect").length;
    return { count: parts.length, totalQty, carsWithParts, orphan, low, defect };
  }, [parts]);

  const toggle = (key) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const expandAll = () => setExpanded(new Set(groups.map((g) => g.carId)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className="space-y-4" data-testid="car-parts-view">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard icon={<Wrench size={14}/>} label="Total piese" value={kpi.count} sub={`Σ ${kpi.totalQty.toFixed(0)} buc`} color="emerald" testid="kpi-parts-total"/>
        <KpiCard icon={<CarIcon size={14}/>} label="Mașini cu piese" value={kpi.carsWithParts} color="blue" testid="kpi-parts-cars"/>
        <KpiCard icon={<Package size={14}/>} label="Universale" value={kpi.orphan} color="zinc" testid="kpi-parts-orphan"/>
        <KpiCard icon={<AlertTriangle size={14}/>} label="Stoc scăzut (≤1)" value={kpi.low} color="amber" testid="kpi-parts-low"/>
        <KpiCard icon={<AlertTriangle size={14}/>} label="Defecte" value={kpi.defect} color="red" testid="kpi-parts-defect"/>
      </div>

      {/* Filters + add */}
      <div className="card-flat p-3 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută piesă, serie, furnizor sau mașină..."
            className="pl-7 h-9 w-[280px]"
            data-testid="parts-search"
          />
        </div>
        <Select value={carFilter} onValueChange={setCarFilter}>
          <SelectTrigger className="h-9 w-[220px]" data-testid="parts-car-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate mașinile</SelectItem>
            <SelectItem value="__none__">— Universale / Nealocate</SelectItem>
            {(cars || []).slice().sort((a, b) => (a.marca || "").localeCompare(b.marca || "", "ro")).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {`${c.marca || ""} ${c.model || ""}`.trim()}{c.numar_inmatriculare ? ` (${c.numar_inmatriculare})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="parts-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate statusurile</SelectItem>
            <SelectItem value="ok">OK (stoc &gt; 1)</SelectItem>
            <SelectItem value="low">Stoc scăzut</SelectItem>
            <SelectItem value="defect">Defect</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={expandAll} data-testid="parts-expand-all">Expand toate</Button>
        <Button variant="outline" size="sm" onClick={collapseAll} data-testid="parts-collapse-all">Collapse</Button>
        <div className="ml-auto">
          <Button
            onClick={() => onAddPart && onAddPart(null)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="parts-add-btn"
          >
            <Plus size={14} className="mr-1.5"/>
            Adaugă piesă auto
          </Button>
        </div>
      </div>

      {/* Groups */}
      {groups.length === 0 ? (
        <div className="card-flat p-12 text-center text-zinc-500 italic" data-testid="parts-empty">
          {parts.length === 0
            ? `Nicio piesă auto înregistrată. Apasă „Adaugă piesă auto" pentru a începe.`
            : "Nicio piesă nu corespunde filtrelor curente."}
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const isOpen = expanded.has(g.carId);
            const totalQty = g.items.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
            const lowN = g.items.filter((it) => (Number(it.quantity) || 0) <= 1).length;
            const isOrphan = g.carId === "__none__";
            return (
              <div
                key={g.carId}
                className={`card-flat overflow-hidden ${isOrphan ? "border-l-4 border-zinc-400" : "border-l-4 border-blue-500"}`}
                data-testid={`parts-group-${g.carId}`}
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                  onClick={() => toggle(g.carId)}
                  data-testid={`parts-group-toggle-${g.carId}`}
                >
                  {isOpen ? <ChevronDown size={16} className="text-zinc-500"/> : <ChevronRight size={16} className="text-zinc-500"/>}
                  {isOrphan ? (
                    <Package size={18} className="text-zinc-500"/>
                  ) : (
                    <CarIcon size={18} className="text-blue-600"/>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
                      <span className="truncate">{g.label}</span>
                      {g.car?.intern_number && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">#{g.car.intern_number}</span>
                      )}
                      {g.car?.culoare && (
                        <span className="text-[10px] text-zinc-500 font-normal">· {g.car.culoare}</span>
                      )}
                    </div>
                    {g.car && (g.car.an_fabricatie || g.car.vin) && (
                      <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                        {g.car.an_fabricatie ? `${g.car.an_fabricatie}` : ""}{g.car.vin ? ` · VIN ${g.car.vin}` : ""}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold tabular-nums">{g.items.length} {g.items.length === 1 ? "piesă" : "piese"}</span>
                    <span className="px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-bold tabular-nums">Σ {totalQty}</span>
                    {lowN > 0 && (
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold tabular-nums flex items-center gap-1">
                        <AlertTriangle size={11}/>
                        {lowN} stoc scăzut
                      </span>
                    )}
                  </div>
                  {!isOrphan && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 h-7 text-[11px]"
                      onClick={(e) => { e.stopPropagation(); onAddPart && onAddPart(g.carId); }}
                      data-testid={`parts-add-for-${g.carId}`}
                    >
                      <Plus size={12} className="mr-0.5"/>
                      la mașină
                    </Button>
                  )}
                </button>
                {isOpen && (
                  <div className="border-t border-zinc-200 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50">
                        <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                          <th className="px-3 py-2">Denumire</th>
                          <th className="px-3 py-2">Categorie</th>
                          <th className="px-3 py-2 text-right">Cantitate</th>
                          <th className="px-3 py-2">Locație</th>
                          <th className="px-3 py-2">Furnizor</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((it) => {
                          const qty = Number(it.quantity) || 0;
                          const lowStock = qty <= 1;
                          const isDefect = it.status === "defect";
                          return (
                            <tr
                              key={it.id}
                              className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                              onClick={() => onEdit && onEdit(it)}
                              data-testid={`parts-row-${it.id}`}
                            >
                              <td className="px-3 py-2">
                                <div className="font-medium">{it.name}</div>
                                <div className="text-[10px] text-zinc-500 flex gap-2 flex-wrap mt-0.5">
                                  {it.serial && <span className="font-mono">SN: {it.serial}</span>}
                                  {it.no_serial && <span className="italic">fără serie</span>}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-[11px] text-zinc-600">
                                {it.equipment_category || <span className="text-zinc-400">—</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums">
                                <span className={lowStock ? "text-amber-700 font-bold" : "text-zinc-800"}>
                                  {qty}{it.unit ? ` ${it.unit}` : ""}
                                </span>
                                {lowStock && <AlertTriangle size={11} className="inline ml-1 text-amber-600"/>}
                              </td>
                              <td className="px-3 py-2 text-[11px] text-zinc-600">
                                {it.location ? `📍 ${it.location}` : <span className="text-zinc-400">—</span>}
                                {it.shelf_row && it.shelf_col && <span className="text-zinc-400"> · raft {it.shelf_row}{it.shelf_col}</span>}
                              </td>
                              <td className="px-3 py-2 text-[11px] text-zinc-600">
                                {it.supplier || <span className="text-zinc-400">—</span>}
                                {it.price_chf != null && <div className="text-[10px] text-emerald-700 font-bold">{Number(it.price_chf).toFixed(2)} CHF</div>}
                              </td>
                              <td className="px-3 py-2">
                                {isDefect ? (
                                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-800">Defect</span>
                                ) : lowStock ? (
                                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Stoc scăzut</span>
                                ) : (
                                  <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">OK</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  className="text-[11px] text-blue-600 hover:underline"
                                  onClick={(e) => { e.stopPropagation(); onEdit && onEdit(it); }}
                                  data-testid={`parts-edit-${it.id}`}
                                >
                                  Editează
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, color = "zinc", testid }) {
  const palette = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    blue: "border-blue-200 bg-blue-50/60 text-blue-900",
    zinc: "border-zinc-200 bg-zinc-50 text-zinc-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-900",
    red: "border-red-200 bg-red-50/60 text-red-900",
  }[color] || "border-zinc-200 bg-zinc-50 text-zinc-800";
  return (
    <div className={`rounded-lg border p-3 ${palette}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl font-extrabold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
